import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Check, Link2, Sparkles, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import PlatformPreview from './PlatformPreview';

const PublishModal = ({ clip, open, onClose }) => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [cta, setCta] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [prefilling, setPrefilling] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [connectingName, setConnectingName] = useState(null);
  const [previewProvider, setPreviewProvider] = useState('tiktok');
  const [results, setResults] = useState(null); // set after submit

  const loadProviders = useCallback(() => {
    setLoadingProviders(true);
    api.get('/social/providers')
      .then((r) => setProviders(r.data.providers || []))
      .catch(() => toast.error(lang === 'fr' ? 'Erreur chargement plateformes' : 'Failed to load platforms'))
      .finally(() => setLoadingProviders(false));
  }, [lang]);

  // Load providers + preload AI content once when modal opens
  useEffect(() => {
    if (!open || !clip?.id) return;
    setResults(null);
    setSelected(new Set());
    loadProviders();

    // Prefill from existing AI outputs (coach caption / hashtags / titles).
    setPrefilling(true);
    const preload = async () => {
      let ok = false;
      try {
        const { data } = await api.get(`/clips/${clip.id}/ai-content`);
        if (data) {
          ok = true;
          setTitle(data.titles?.[0] || clip.title || '');
          setCaption(data.caption_tiktok || data.caption_instagram || data.caption_linkedin || clip.caption_preview || '');
          setHashtags((data.hashtags || []).slice(0, 12).map((h) => `#${h.replace(/^#/, '')}`).join(' '));
          setCta(data.ctas?.[0] || (lang === 'fr' ? 'Abonne-toi pour plus 💡' : 'Follow for more 💡'));
        }
      } catch (_) { /* not generated yet — fall back to defaults below */ }
      if (!ok) {
        setTitle(clip.title || '');
        setCaption(clip.caption_preview || '');
        setHashtags((clip.keywords || []).map((k) => `#${k}`).join(' '));
        setCta(lang === 'fr' ? 'Abonne-toi pour plus 💡' : 'Follow for more 💡');
      }
      setPrefilling(false);
    };
    preload();
  }, [open, clip?.id, clip?.title, clip?.caption_preview, clip?.keywords, lang, loadProviders]);

  const parsedHashtags = useMemo(
    () => hashtags.split(/[\s,]+/).map((h) => h.trim().replace(/^#/, '')).filter(Boolean),
    [hashtags],
  );

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const connectPlatform = async (name) => {
    setConnectingName(name);
    try {
      const { data } = await api.post(`/social/providers/${name}/connect`);
      // V1 mock: the returned authorize_url points to a same-origin mock-callback we can just GET.
      await api.get(data.authorize_url.replace(/^\/api/, ''));
      toast.success(lang === 'fr' ? `${name} connecté (mock)` : `${name} connected (mock)`);
      loadProviders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connect failed');
    } finally {
      setConnectingName(null);
    }
  };

  const submit = async () => {
    if (selected.size === 0) {
      toast.error(lang === 'fr' ? 'Sélectionne au moins une plateforme' : 'Select at least one platform');
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post('/social/publications', {
        clip_id: clip.id,
        platforms: Array.from(selected),
        title: title.trim() || null,
        caption: caption.trim(),
        hashtags: parsedHashtags,
        cta: cta.trim() || null,
      });
      setResults(data.publications || []);
      const successes = (data.publications || []).filter((p) => p.status === 'published').length;
      const failures = (data.publications || []).filter((p) => p.status === 'failed').length;
      if (successes && !failures) toast.success(lang === 'fr' ? `Publié sur ${successes} plateforme(s)` : `Published to ${successes} platform(s)`);
      else if (successes && failures) toast.info(lang === 'fr' ? `${successes} publié · ${failures} échoué` : `${successes} published · ${failures} failed`);
      else toast.error(lang === 'fr' ? 'Aucune publication réussie' : 'No successful publications');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  if (!open) return null;

  const providerByName = Object.fromEntries(providers.map((p) => [p.name, p]));
  const previewData = { title, caption, hashtags: parsedHashtags, cta, user };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="publish-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0a]"
            onClick={(e) => e.stopPropagation()}
            data-testid="publish-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#0a0a0a] z-10">
              <div>
                <h2 className="font-heading font-black text-2xl leading-none">
                  {lang === 'fr' ? 'Publier ce clip' : 'Publish this clip'}
                </h2>
                <p className="text-xs text-zinc-500 mt-1 font-mono line-clamp-1">{clip?.title}</p>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-lg border border-white/10 hover:bg-white/5 flex items-center justify-center"
                data-testid="publish-modal-close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {results ? (
              // Result screen after submit
              <div className="p-8 space-y-4" data-testid="publish-results">
                <h3 className="font-heading font-bold text-lg mb-2">
                  {lang === 'fr' ? 'Résultats' : 'Results'}
                </h3>
                {results.map((r) => (
                  <div
                    key={r.id}
                    className={`rounded-xl border p-4 flex items-center gap-3 ${
                      r.status === 'published' ? 'border-[#22C55E]/30 bg-[#22C55E]/[0.05]' :
                      r.status === 'scheduled' ? 'border-[#00E5FF]/30 bg-[#00E5FF]/[0.05]' :
                      'border-red-500/30 bg-red-500/[0.05]'
                    }`}
                    data-testid={`publish-result-${r.platform}`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                         style={{ background: (providerByName[r.platform]?.accent_color || '#888') + '20',
                                  border: `1px solid ${(providerByName[r.platform]?.accent_color || '#888')}40` }}>
                      {r.status === 'published' ? <Check className="w-4 h-4 text-[#22C55E]" /> :
                       r.status === 'scheduled' ? <Sparkles className="w-4 h-4 text-[#00E5FF]" /> :
                       <AlertCircle className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{providerByName[r.platform]?.display_name || r.platform}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {r.status === 'published' ? r.external_url :
                         r.status === 'failed' ? r.error :
                         (lang === 'fr' ? 'Programmé' : 'Scheduled')}
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded"
                          style={{
                            color: r.status === 'published' ? '#22C55E' :
                                   r.status === 'scheduled' ? '#00E5FF' : '#EF4444',
                            border: `1px solid currentColor`
                          }}>
                      {r.status}
                    </span>
                    {r.external_url && (
                      <a href={r.external_url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-white" data-testid={`publish-result-open-${r.platform}`}>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <Button onClick={onClose} className="flex-1 bg-white/10 hover:bg-white/15 text-white" data-testid="publish-modal-done">
                    {lang === 'fr' ? 'Terminé' : 'Done'}
                  </Button>
                  <a href="/social/history" className="flex-1">
                    <Button variant="outline" className="w-full border-white/10 text-zinc-300" data-testid="publish-modal-view-history">
                      {lang === 'fr' ? 'Voir l\u2019historique' : 'View history'}
                    </Button>
                  </a>
                </div>
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Left column: editor */}
                <div className="p-6 space-y-6 border-r border-white/5">
                  {/* Platform picker */}
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3 block">
                      {lang === 'fr' ? 'Plateformes' : 'Platforms'}
                    </label>
                    {loadingProviders ? (
                      <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
                    ) : (
                      <div className="space-y-2" data-testid="publish-platforms">
                        {providers.map((p) => {
                          const isSelected = selected.has(p.name);
                          const connected = p.connected;
                          return (
                            <div
                              key={p.name}
                              className={`rounded-xl border p-3 flex items-center gap-3 transition ${
                                isSelected ? 'border-[#D4FF00]/50 bg-[#D4FF00]/[0.03]' : 'border-white/10 bg-white/[0.02]'
                              }`}
                              data-testid={`publish-platform-${p.name}`}
                            >
                              <button
                                type="button"
                                onClick={() => connected ? toggle(p.name) : null}
                                disabled={!connected}
                                className={`w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center ${
                                  !connected ? 'border-white/10 cursor-not-allowed' :
                                  isSelected ? 'border-[#D4FF00] bg-[#D4FF00]' : 'border-white/30'
                                }`}
                                data-testid={`publish-platform-toggle-${p.name}`}
                              >
                                {isSelected && <Check className="w-3 h-3 text-black" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.accent_color }} />
                                  <span className="text-sm font-semibold">{p.display_name}</span>
                                </div>
                                <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                                  {connected ? (lang === 'fr' ? 'Connecté' : 'Connected') : (lang === 'fr' ? 'Non connecté' : 'Not connected')}
                                </div>
                              </div>
                              {connected ? (
                                <button
                                  type="button"
                                  onClick={() => setPreviewProvider(p.name)}
                                  className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded border ${previewProvider === p.name ? 'border-[#D4FF00] text-[#D4FF00]' : 'border-white/10 text-zinc-500 hover:text-white'}`}
                                  data-testid={`publish-platform-preview-${p.name}`}
                                >
                                  Preview
                                </button>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => connectPlatform(p.name)}
                                  disabled={connectingName === p.name}
                                  className="h-8 bg-white text-black hover:bg-white/90 text-[11px] font-semibold"
                                  data-testid={`publish-platform-connect-${p.name}`}
                                >
                                  {connectingName === p.name ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Link2 className="w-3 h-3 mr-1" /> {lang === 'fr' ? 'Connecter' : 'Connect'}</>}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* AI-preloaded content, editable */}
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
                          {lang === 'fr' ? 'Titre' : 'Title'}
                        </label>
                        {prefilling && <span className="text-[10px] text-zinc-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> AI</span>}
                      </div>
                      <input
                        type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder={lang === 'fr' ? 'Ton titre accrocheur' : 'Your hook title'}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-[#D4FF00]/40"
                        data-testid="publish-input-title"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2 block">
                        {lang === 'fr' ? 'Légende' : 'Caption'}
                      </label>
                      <textarea
                        value={caption} onChange={(e) => setCaption(e.target.value)}
                        rows={4} placeholder={lang === 'fr' ? 'Ta légende…' : 'Your caption…'}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-[#D4FF00]/40 resize-none"
                        data-testid="publish-input-caption"
                      />
                      <div className="text-[10px] text-zinc-600 mt-1 font-mono text-right">{caption.length} chars</div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2 block">Hashtags</label>
                      <input
                        type="text" value={hashtags} onChange={(e) => setHashtags(e.target.value)}
                        placeholder="#viral #tiktok"
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-[#D4FF00]/40 font-mono"
                        data-testid="publish-input-hashtags"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2 block">CTA</label>
                      <input
                        type="text" value={cta} onChange={(e) => setCta(e.target.value)}
                        placeholder={lang === 'fr' ? 'Ton appel à l\u2019action' : 'Your call to action'}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-[#D4FF00]/40"
                        data-testid="publish-input-cta"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={submit}
                    disabled={publishing || selected.size === 0}
                    className="w-full bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-11"
                    data-testid="publish-submit"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {lang === 'fr' ? `Publier sur ${selected.size} plateforme(s)` : `Publish to ${selected.size} platform(s)`}
                  </Button>
                </div>

                {/* Right column: preview */}
                <div className="p-6 bg-white/[0.01]">
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Preview</label>
                    <div className="flex gap-1" data-testid="publish-preview-tabs">
                      {providers.map((p) => (
                        <button
                          key={p.name}
                          type="button"
                          onClick={() => setPreviewProvider(p.name)}
                          className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded ${previewProvider === p.name ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'}`}
                          data-testid={`publish-preview-tab-${p.name}`}
                        >
                          {p.display_name.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <PlatformPreview provider={previewProvider} clip={clip} data={previewData} />
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default PublishModal;
